import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, Download, Timer } from "lucide-react";
import { toast } from "sonner";

const LEAD_MAGNET_URL = "/api/static/assets/c9e578ba_6rpbg1xu_PACT_From-Conflict-to-Connection-Leadership-Guide.pdf";
const COUPON_CODE = "LEADER20";
const COUPON_TTL_SECONDS = 60 * 60;

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const ThankYou = () => {
  const [remaining, setRemaining] = useState(COUPON_TTL_SECONDS);

  useEffect(() => {
    const t = setInterval(() => setRemaining((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(COUPON_CODE); toast.success("Code copied"); }
    catch (e) { toast.error("Copy failed — please copy manually"); }
  };

  const expired = remaining === 0;

  return (
    <div data-testid="thank-you-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
      <div className="text-center">
        <CheckCircle className="w-16 h-16 text-[#8C4A57] mx-auto mb-6" />
        <div className="text-[10px] tracking-[0.32em] uppercase text-[#8C4A57] mb-3">Thank you</div>
        <h1 className="text-4xl sm:text-5xl font-serif font-semibold text-[#2D2628] mb-5 leading-tight">
          Your guide is on the way
        </h1>
        <p className="text-[#6B5E62] leading-relaxed mb-2">
          Your free preview — <em>PACT: From Conflict to Connection</em> Leadership Guide —
          has opened in a new tab. If it didn&apos;t, use the button below.
        </p>
        <p className="text-sm text-[#6B5E62] mb-8">
          A copy has also been recorded with your subscription — welcome to the community.
        </p>

        <div className="flex flex-wrap justify-center gap-3 mb-12">
          <a href={LEAD_MAGNET_URL} target="_blank" rel="noopener noreferrer"
            data-testid="thank-you-download-link" className="btn-primary">
            <Download className="w-4 h-4 mr-2" /> Download PDF
          </a>
          <Link to="/" data-testid="thank-you-home-link" className="btn-outline">Back to home</Link>
        </div>
      </div>

      {/* Timed discount */}
      <div data-testid="thank-you-coupon-block"
        className={`rounded-md p-8 sm:p-10 text-center transition-colors duration-300 ${
          expired ? "bg-[#FAF4F5] border border-[#E8DCDD]" : "bg-[#8C4A57] text-white"
        }`}>
        <div className={`inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase rounded-sm px-3 py-1 mb-4 ${
          expired ? "bg-white border border-[#E8DCDD] text-[#6B5E62]" : "bg-white/10 border border-white/20"
        }`}>
          <Timer className="w-3 h-3" /> {expired ? "Offer expired" : "Limited-time reader bonus"}
        </div>
        <h2 className={`text-2xl sm:text-3xl font-serif font-semibold mb-3 ${expired ? "text-[#2D2628]" : ""}`}>
          20% off <em>Personal Clarity to Organizational Impact</em>
        </h2>
        <p className={`leading-relaxed max-w-xl mx-auto mb-6 ${expired ? "text-[#6B5E62]" : "text-white/85"}`}>
          A reader-only thank-you: use the code below at checkout on the <em>Personal Clarity to
          Organizational Impact</em> ebook. This offer disappears when the timer hits zero.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-5">
          <div className={`px-6 py-3 rounded-sm font-mono tracking-widest text-xl border-2 border-dashed ${
            expired ? "border-[#E8DCDD] text-[#6B5E62] bg-white" : "border-white/40 bg-white/10"
          }`} data-testid="coupon-code">{COUPON_CODE}</div>
          <button onClick={copyCode} disabled={expired} data-testid="coupon-copy-btn"
            className={`px-5 py-3 rounded-sm text-xs font-semibold uppercase tracking-wide transition-colors duration-200 disabled:opacity-50 ${
              expired ? "border border-[#8C4A57] text-[#8C4A57] hover:bg-[#8C4A57] hover:text-white"
                : "bg-white text-[#8C4A57] hover:bg-white/90"
            }`}>Copy code</button>
        </div>

        <div className={`text-sm font-mono tracking-widest mb-6 ${expired ? "text-[#6B5E62]" : "text-white/85"}`}
          data-testid="coupon-timer">
          {expired ? "00:00" : fmt(remaining)}
        </div>

        <Link to="/ebooks?product=eb_leadership_1" data-testid="thank-you-shop-link"
          className={expired ? "btn-outline" : "inline-flex items-center justify-center bg-white text-[#8C4A57] hover:bg-white/90 px-6 py-3 rounded-sm text-sm font-semibold uppercase tracking-wide transition-colors duration-200"}>
          Shop Personal Clarity → Organizational Impact
        </Link>
      </div>
    </div>
  );
};

export default ThankYou;
